/// <amd-module name="Mention/Autocomplete/AutocompleteInputProxy" />

export function isAutoCompleteInputProxy(value: unknown): value is IAutocompleteInputProxy {
    const requiredFunctions: (keyof IAutocompleteInputProxy)[] = [
        "getSelection",
        "insertCalloutAnchor",
        "insertText",
        "addKeyDownListener",
        "addKeyUpListener",
        "addInputListener",
        "addBlurListener",
        "addPasteListener",
        "isTargetedByEvent",
        "setInputAttribute",
        "removeInputAttribute",
        "focus"
    ];
    return (
        !!value &&
        typeof value === "object" &&
        requiredFunctions.reduce((all, fnName) => all && typeof (value as IAutocompleteInputProxy)[fnName] === "function", true)
    );
}

/**
 * Provides all the methods needed for the AutocompleteComponent to interact with the element to which is it bound.
 * No direct reference to the element itself is needed.
 */
export interface IAutocompleteInputProxy {
    getElementForAnchor(): HTMLElement;
    getSelection(): IAutocompleteInputSelection;
    insertCalloutAnchor(anchor: HTMLElement): void;
    insertText(result: string, startIndex: number): void;
    getClientWidth: () => number;

    addKeyDownListener(onKeyDown: (e: KeyboardEvent) => void): void;
    addKeyUpListener(onKeyUp: (e: KeyboardEvent) => void): void;
    addInputListener(onInput: () => void): void;
    addBlurListener(onBlur: (e: FocusEvent) => void): void;
    addPasteListener(onPaste: (e: ClipboardEvent) => void): void;
    removeListeners(): void;
    isTargetedByEvent(ev?: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): boolean;

    setInputAttribute: HTMLElement["setAttribute"];
    getInputAttribute: HTMLElement["getAttribute"];
    removeInputAttribute: HTMLElement["removeAttribute"];
    focus: HTMLElement["focus"];
}

export function createAutocompleteInputProxy(
    htmlElement: HTMLTextAreaElement | HTMLDivElement,
    onInputChange?: (newContent: string) => void
): IAutocompleteInputProxy {
    if (htmlElement instanceof HTMLTextAreaElement) {
        return new TextAreaInputElement(htmlElement, onInputChange);
    } else if (htmlElement instanceof HTMLDivElement) {
        return new DivInputElement(htmlElement);
    }

    throw new Error("Unsupported input element");
}

export interface IAutocompleteInputSelection {
    selectionStart: number;
    selectionEnd: number;
    value: string;
}

/**
 * Provides basic methods for the AutocompleteComponent to interact with an html element
 */
abstract class HTMLElementInputElement<T extends HTMLElement> implements IAutocompleteInputProxy {
    protected _inputElement: T;
    private _listeners: { eventName: string; listener: (e: unknown) => void }[] = [];

    constructor(htmlElement: T) {
        this._inputElement = htmlElement;
    }

    /* Leave these methods to implementations for particular elements since there is no generic way to do these */
    public abstract getSelection(): IAutocompleteInputSelection;
    public abstract insertCalloutAnchor(anchor: HTMLElement): void;
    public abstract insertText(result: string, startIndex: number): void;

    /* Delegate these methods directly to the html element */
    public getElementForAnchor = () => this._inputElement;
    public getClientWidth = () => this._inputElement.clientWidth;
    public setInputAttribute: HTMLElement["setAttribute"] = (name, value) => this._inputElement.setAttribute(name, value);
    public getInputAttribute: HTMLElement["getAttribute"] = (name) => this._inputElement.getAttribute(name);
    public removeInputAttribute: HTMLElement["removeAttribute"] = (name) => this._inputElement.removeAttribute(name);
    public focus: HTMLElement["focus"] = () => this._inputElement.focus();

    public addKeyDownListener(onKeyDown: (e: KeyboardEvent) => void) {
        this._listeners.push({ eventName: "keydown", listener: onKeyDown });
        this._inputElement.addEventListener("keydown", onKeyDown);
    }

    public addKeyUpListener(onKeyUp: (e: KeyboardEvent) => void) {
        this._listeners.push({ eventName: "keyup", listener: onKeyUp });
        this._inputElement.addEventListener("keyup", onKeyUp);
    }

    public addInputListener(onInput: () => void) {
        this._listeners.push({ eventName: "input", listener: onInput });
        this._inputElement.addEventListener("input", onInput);
    }

    public addBlurListener(onBlur: (e: FocusEvent) => void) {
        this._listeners.push({ eventName: "blur", listener: onBlur });
        this._inputElement.addEventListener("blur", onBlur);
    }

    public addPasteListener(onPaste: (e: ClipboardEvent) => void) {
        this._listeners.push({ eventName: "paste", listener: onPaste });
        this._inputElement.addEventListener("paste", onPaste);
    }

    public removeListeners() {
        for (const { eventName, listener } of this._listeners) {
            this._inputElement.removeEventListener(eventName, listener);
        }
    }

    public isTargetedByEvent(ev?: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) {
        return !!ev && ev.target === this._inputElement;
    }
}

/**
 * Provides methods for the AutocompleteComponent to interact with an <input/> element
 */
class TextAreaInputElement extends HTMLElementInputElement<HTMLTextAreaElement> {
    private _shadowInput: HTMLDivElement = document.createElement("div");
    private _onInputChange: undefined | ((newContent: string) => void);

    public constructor(htmlElement: HTMLTextAreaElement, onInputChange?: (newContent: string) => void) {
        super(htmlElement);
        this._onInputChange = onInputChange;

        this._setShadowStyle();
        this._inputElement.insertAdjacentElement("beforebegin", this._shadowInput);
    }

    public getSelection(): IAutocompleteInputSelection {
        const { selectionStart, selectionEnd, value } = this._inputElement;
        return { selectionStart, selectionEnd, value };
    }

    public insertCalloutAnchor(anchor: HTMLElement): void {
        this._shadowInput.innerHTML = this._inputElement.innerHTML;
        this._setShadowStyle();
        const { selectionStart, selectionEnd } = this.getSelection();
        const range = document.createRange();
        range.setStart(this._shadowInput.childNodes[0], selectionStart);
        range.setEnd(this._shadowInput.childNodes[0], selectionEnd);
        const border = range.getBoundingClientRect() as DOMRect;
        anchor.style.top = border.top + "px";
        anchor.style.left = border.left + "px";
        anchor.style.height = border.height + "px";
        anchor.style.position = "absolute";
        document.body.insertAdjacentElement("afterbegin", anchor);

        this._inputElement.selectionStart = selectionStart;
        this._inputElement.selectionEnd = selectionEnd;
        this._inputElement.focus();
    }

    public insertText(result: string, startIndex: number): void {
        const { value, selectionStart } = this.getSelection();
        const insertTextCommand = "insertText";
        if (this._browserSupportsInsertText()) {
            this._inputElement.selectionEnd = this._inputElement.selectionStart;
            this._inputElement.selectionStart = startIndex;
            document.execCommand(insertTextCommand, false, result);
        } else {
            // IE11 and FF
            const newContent = `${value.substr(0, startIndex)}${result}${value.substr(selectionStart)}`;
            if (this._onInputChange) {
                this._onInputChange(newContent);
            } else {
                this._inputElement.value = newContent;
            }

            this._inputElement.selectionEnd = this._inputElement.selectionStart = startIndex + result.length;
        }
    }

    /**
     * Tests whether insertText is supported in the browser.
     * insertText programatically programatically change the text which supports undo/redo
     */
    private _browserSupportsInsertText(): boolean {
        // ie does not support it and correctly reports that it does not support it.
        // firefox claims to support it... but lies.
        // see https://bugzilla.mozilla.org/show_bug.cgi?id=1220696
        if (this._isFirefox() || !document.queryCommandSupported("insertText")) {
            return false;
        }

        return true;
    }

    private _isFirefox(): boolean {
        const userAgent = window.navigator.userAgent.toLowerCase();
        const mozilla = /mozilla.*rv:([\d+.]+)/i.exec(navigator.userAgent);

        return !!mozilla && userAgent.indexOf("trident") === -1;
    }

    private _setShadowStyle(): void {
        const inputStyles = getComputedStyle(this._inputElement);
        this._shadowInput.style.cssText = inputStyles.cssText;
        // TODO: shadowElement won't place its text correctly when the textarea has a scroll position.
        // Sometimes callout won't place on the first call, but if you delete and re-insert the key character it will appear
        this._shadowInput.scrollLeft = this._inputElement.scrollLeft;
        this._shadowInput.scrollTop = this._inputElement.scrollTop;
        this._shadowInput.style.position = "absolute";
        this._shadowInput.style.visibility = "hidden";
    }
}

/**
 * Provides methods for the AutocompleteComponent to interact with a <div/> element
 */
class DivInputElement extends HTMLElementInputElement<HTMLDivElement> {
    private _mentionRow?: Node;

    public constructor(htmlElement: HTMLDivElement) {
        super(htmlElement);
    }

    public getSelection(): IAutocompleteInputSelection {
        const selection = window.document.getSelection();
        if (selection) {
            const text = selection.focusNode ? selection.focusNode.textContent : "";
            return { selectionStart: selection.anchorOffset, selectionEnd: selection.anchorOffset, value: text || "" };
        }
        return { selectionStart: 0, selectionEnd: 0, value: this._inputElement.innerText };
    }

    public insertCalloutAnchor(anchor: HTMLElement): void {
        const sel = window.getSelection();
        if (sel) {
            this._mentionRow = sel.focusNode || undefined;
            const range = sel.getRangeAt(0);
            range.insertNode(anchor);
        }
    }

    public insertText(result: string, startIndex: number): void {
        const { selectionStart } = this.getSelection();

        // Insert text into the last active row
        let rowNode: Node = this._mentionRow || this._inputElement.childNodes[0];
        const text = rowNode.textContent || "";
        rowNode.textContent = `${text.substr(0, startIndex)}${result}${text.substr(selectionStart)}`;

        // Move the caret to the end of the mention text
        const selection = window.document.getSelection();
        if (selection) {
            const range = document.createRange();
            range.setStart(rowNode, startIndex + result.length - 1);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }
}
